<template>
  <div>
    <div
      v-if="hasDiscriminativeStimulus"
      class="row full-width bg-primary-variant text-white q-px-sm q-py-xs"
    >
      {{ target.discriminativeStimulus }}
    </div>
    <div class="row text-center full-width q-px-sm q-pt-sm">
      <div
        :class="{
          'text-uppercase text-dark-variant task-analysis-overline': tiny,
        }"
        class="text-overline row items-center"
      >
        <q-chip
          v-if="!tiny"
          size="sm"
          :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
          :color="getTargetWorkflowProperty('color', '')"
          :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
          :icon="getTargetWorkflowProperty('icon', 'help')"
          class="chip-no-label"
        />
        {{ target.label }}
      </div>
    </div>
    <div
      :class="{'no-wrap': dense && tiny}"
      class="row q-pa-sm q-col-gutter-sm"
    >
      <span
        v-for="subTarget in sortedTargets"
        :key="subTarget.id"
      >
        <target-data
          ref="targets"
          :session-program="sessionProgram"
          :target="subTarget"
          :data="data"
          :active-prompt-levels="activePromptLevels"
          :dense="dense"
          :tiny="tiny"
          :disable="disable"
          :focused="isFocusedSuggestion(subTarget)"
          :dimmed="isDimmedSubTarget(subTarget)"
          :workflow-statuses="workflowStatuses"
          :prevent-duplicates-on-trial="preventDuplicatesOnTrial"
          :session-paused="sessionPaused"
          :session-duration-offset="sessionDurationOffset"
          @record="onRecordData"
          @record-program-data="onRecordProgramData"
        />
      </span>
    </div>
  </div>
</template>
<script>

import {
  chain, concat, find, findIndex, get, head,
  includes, map,
} from 'lodash';
import DataCollection from 'src/mixins/DataCollection';
import { defineAsyncComponent } from 'vue';
import { getTextColor } from 'src/utils/TargetUtils';

export default {

  name: 'TaskAnalysisTarget',

  components: { TargetData: defineAsyncComponent(() => import('components/Programs/DataCollection/TargetData')) },

  mixins: [DataCollection],

  props: {
    cachedCurrentProgramTrial: {
      type: Number,
      default: 0,
    },
  },

  emits: ['record-target', 'record-program-data'],

  data() {
    return {
      pauseSuggestion: false,
      currentSuggestion: 3,

      /**
       * Cached data ensures the right calculation of the trials on collecting targets with default observation and
       * through multiple observation window.
       */
      cache: {
        currentTrialNumber: 0,
        lastTargetTrialNumber: null,
        lastTaskIndex: -1,
      },
    };
  },

  computed: {

    sortedTargets() {
      return get(this.target, 'subTargets', []);
    }, // sortedTargets()

    defaultPromptLevel() {
      return find(this.subTargetProgramPrompts, { id: get(this.target, 'defaultPromptLevel', null) });
    }, // defaultPromptLevel()

    nextTrialNumber() {
      return this.cache.currentTrialNumber + 1;
    }, // nextTrialNumber()

    currentTrialNumber() {
      return this.cache.lastTargetTrialNumber || this.nextTrialNumber;
    }, // currentTrialNumber()

    /** @type {Object | null} */
    lastTrial() {
      const sortedTargetIds = map(this.sortedTargets, 'id');
      return chain(this.data)
        .get('data')
        .filter((dataItem) => includes(sortedTargetIds, dataItem.target))
        .last()
        .defaultTo(null)
        .value();
    }, // lastTrial()

    lastTaskIndex() {
      if (this.lastTrial === null) {
        return -1;
      }
      return findIndex(this.sortedTargets, { id: get(this.lastTrial, 'target') });
    }, // lastTrial()

    nextTask() {
      if (this.lastTrial === null) {
        return head(this.sortedTargets);
      }
      const lastTaskOrder = chain(this.sortedTargets)
        .find({ id: get(this.lastTrial, 'target') })
        .get('order', 0)
        .value();

      return chain(this.sortedTargets)
        .filter((target) => !['waiting', 'closed'].includes(target.status) && (target.order > lastTaskOrder))
        .head()
        .defaultTo(head(this.sortedTargets))
        .value();
    }, // nextTask()
  },

  watch: {
    /**
     * Make sure that cache data is synced with cloud data.
     */
    cachedCurrentProgramTrial: {
      immediate: true,
      handler(val) {
        this.cache.currentTrialNumber = val;
      },
    },
    lastTrial: {
      immediate: true,
      handler(val) {
        if (val) {
          this.cache.lastTargetTrialNumber = val.trial;
        }
      },
    },
    lastTaskIndex: {
      immediate: true,
      handler(val) {
        this.cache.lastTaskIndex = val;
      },
    },
  },

  methods: {
    getTextColor,

    finishSession() {
      let finalData = [];
      if (this.defaultPromptLevel && (this.cache.lastTaskIndex !== -1)) {
        // Record default data for end parts of the previous chain

        finalData = chain(this.sortedTargets)
          .filter((preliminaryTarget, index) => (
            (index > this.cache.lastTaskIndex)
          ))
          .map((preliminaryTarget) => (
            {
              target: preliminaryTarget.id,
              promptLevel: this.defaultPromptLevel.id,
              scoreValue: this.defaultPromptLevel.score,
              trial: this.currentTrialNumber,
            }))
          .value();
      }

      return concat(
        finalData,
        chain(this.$refs)
          .get('targets')
          .map((targetRef) => {
            if (chain(targetRef).get('finishSession').isFunction().value()) {
              return targetRef.finishSession();
            }
            return null;
          })
          .flatten()
          .compact()
          .value(),
      );
    }, // finishSession()

    onRecordData(sessionProgram, target, data) {
      // Set defaults
      let { currentTrialNumber } = this;

      const currentTargetIndex = findIndex(this.sortedTargets, { id: target.id });
      if (currentTargetIndex <= this.cache.lastTaskIndex) {
        if (this.defaultPromptLevel) {
          // Record default data for end parts of the previous chain
          chain(this.sortedTargets)
            .filter((preliminaryTarget, index) => (
              (index > this.cache.lastTaskIndex)
            ))
            .forEach((preliminaryTarget) => {
              const saveIntermediaryData = {
                promptLevel: this.defaultPromptLevel.id,
                scoreValue: this.defaultPromptLevel.score,
                trial: get(data, 'trial', currentTrialNumber),
              };
              this.$emit('record-target', preliminaryTarget, saveIntermediaryData);
            })
            .value();
        }
        // Increase trial and reset last trial to 0
        currentTrialNumber = this.nextTrialNumber;
        this.cache.lastTaskIndex = -1;
      }

      if (this.defaultPromptLevel && currentTargetIndex > this.cache.lastTaskIndex + 1) {
        // Record default data for missing parts in the middle of the chain (same trial)
        chain(this.sortedTargets)
          .filter((preliminaryTarget, index) => (
            (index > this.cache.lastTaskIndex)
            && (index < currentTargetIndex)
          ))
          .forEach((preliminaryTarget) => {
            const saveIntermediaryData = {
              promptLevel: this.defaultPromptLevel.id,
              scoreValue: this.defaultPromptLevel.score,
              trial: get(data, 'trial', currentTrialNumber),
            };
            this.$emit('record-target', preliminaryTarget, saveIntermediaryData);
          })
          .value();
      }

      const saveData = { ...data, trial: get(data, 'trial', currentTrialNumber) };
      this.$emit('record-target', target, saveData);

      this.cache.lastTaskIndex = currentTargetIndex;
      this.cache.lastTargetTrialNumber = currentTrialNumber;
      if (currentTrialNumber > this.cache.currentTrialNumber) {
        this.cache.currentTrialNumber = currentTrialNumber;
      }
    }, // onRecordData()

    onRecordProgramData(sessionProgram, dataObj) {
      this.$emit('record-program-data', sessionProgram, dataObj);
    }, // onRecordProgramData()

    isFocusedSuggestion(subTarget) {
      if (!this.focused) {
        return false;
      }
      return subTarget.id === get(this.nextTask, 'id');
    }, // isFocusedSuggestion()

    isDimmedSubTarget(subTarget) {
      if (!this.focused) {
        return false;
      }
      return subTarget.order > get(this.nextTask, 'order');
    }, // isDimmedSubTarget()

  },
};
</script>
<style scoped>

.task-analysis-overline {
  font-size: 7px;
  line-height:1rem;
}

</style>
